import Backbone from 'backbone'
import RestConnectionWizardView
	, {TOKEN_OPTIONS} from 'scoreboard/admin/connections/rest/rest-connection-wizard-view'
import template from 'scoreboard/admin/connections/rest/rest-connection-refresh-token.hbs'
import FormView from 'scoreboard/components/form-view'
import formHelper from 'scoreboard/components/form-helper'
import _ from 'lodash'
import {
	decodeUrlParameters, encodeHttpRequestHeadersFromObject,
	encodeUrlParametersFromObject, getHttpHeadersAsObject
} from 'scoreboard/components/http-utils'
import WsConstants from 'scoreboard/admin/model/ws-constants'
const {
	AUTHORIZATION_HEADER, GRANT_TYPE_URL_PARAMETER,
	REFRESH_TOKEN_PLACEHOLDER, REFRESH_TOKEN_URL_PARAMETER
} = WsConstants
import HttpRequestForm from 'scoreboard/components/http-request-form'
import WsAuthenticationType from 'scoreboard/admin/model/ws-authentication-type'
const {OAUTH_V2} = WsAuthenticationType
import globals from 'scoreboard/components/globals'


const RestConnectionRefreshToken = Backbone.View.extend(/** @lends {RestConnectionRefreshToken} */{

	tagName: 'form',

	className: 'rest-properties-dialog',

	/**
	 * The third step in the rest-connection-wizard, only if the user is setting up an
	 * OAuth2 connection, and the /token endpoint returned a refresh token.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Connection} options.model
	 *
	 * @param {Wizard} options.wizard the current wizard
	 */
	initialize: function(options) {
		this.options = options
		this.formView = new FormView({
			el: this.el,
			allowCleanSubmission: true,
			submit: () => this.submit(),
		})
	},

	render: function() {
		RestConnectionWizardView.prototype.render.apply(this, arguments)
		this.$('.dialog-body').replaceWith(template())
		this.renderHttpRequestForm()
		formHelper.writeToDom(this.model, this.el)
		return this
	},

	renderHttpRequestForm: function() {
		const options = this._getHttpRequestFormOptions()
		this.httpRequestForm = new HttpRequestForm(options).render()
		this.$('.http-request-form').replaceWith(this.httpRequestForm.$el)
	},

	_getHttpRequestFormOptions: function() {
		const options = RestConnectionWizardView.prototype.getHttpRequestFormOptions
			.call(this, TOKEN_OPTIONS.REFRESH_TOKEN)

		// only prefill with defaults if the user is setting up the import connection for
		// the first time
		if (!_.isNil(this.model.id)) return options

		const attrs = this.model.attributes

		_.extend(options, this._getDefaultUrlAndMethodOptions())

		// default the URL parameters to the ones from the access token endpoint
		if (_.isNil(attrs.refreshTokenEndpointUrlParameters) &&
			!_.isNil(attrs.accessTokenEndpointUrlParameters)) {
			_.extend(options, this._getDefaultUrlParametersOptions())
			// we only want to fill out one of these sections with defaults, since it's
			// highly unlikely that a token endpoint would have both URL parameters and
			// HTTP headers/request body
			return options
		}
		// default the request headers to the ones from the access token endpoint
		if (_.isNil(attrs.refreshTokenEndpointHttpHeaders) &&
			!_.isNil(attrs.accessTokenEndpointHttpHeaders)) {
			_.extend(options, this._getDefaultHttpHeadersOptions(),)
			return options
		}
		// default the request body JSON to the one from the access token endpoint
		if (_.isNil(attrs.refreshTokenEndpointRequestBody) &&
			!_.isNil(attrs.accessTokenEndpointRequestBody)) {
			_.extend(options, this._getDefaultRequestBodyOptions())
			return options
		}

		return options
	},

	_getDefaultUrlAndMethodOptions: function() {
		const options = {}
		const attrs = this.model.attributes
		// If the user didn't specify a refresh token endpoint, we'll default to the access
		// token endpoint, because most of the time they are the same endpoint with different
		// parameters
		if (_.isNil(attrs.refreshTokenEndpoint)) {
			options.url = attrs.accessTokenEndpoint
		}
		if (_.isNil(attrs.refreshTokenHttpMethod)) {
			options.httpMethod = attrs.accessTokenHttpMethod

		}
		return options
	},

	_getDefaultUrlParametersOptions: function() {
		const options = {}
		const attrs = this.model.attributes
		const urlParams =
			decodeUrlParameters(attrs.accessTokenEndpointUrlParameters) ?? {}
		// add the refresh_token url parameter and our placeholder that will
		// be replaced on the backend
		urlParams[REFRESH_TOKEN_URL_PARAMETER] = REFRESH_TOKEN_PLACEHOLDER
		urlParams[GRANT_TYPE_URL_PARAMETER] = REFRESH_TOKEN_URL_PARAMETER
		options.urlParameters = encodeUrlParametersFromObject(urlParams)

		return options
	},

	_getDefaultHttpHeadersOptions: function() {
		const options = {}
		const attrs = this.model.attributes
		const httpHeaders =
			getHttpHeadersAsObject(attrs.accessTokenEndpointHttpHeaders) ?? {}
		// add the refresh_token url parameter and our placeholder that will
		// be replaced on the backend
		httpHeaders[AUTHORIZATION_HEADER] = REFRESH_TOKEN_PLACEHOLDER
		options.httpRequestHeaders = encodeHttpRequestHeadersFromObject(httpHeaders)

		return options
	},

	_getDefaultRequestBodyOptions: function() {
		const options = {}
		const attrs = this.model.attributes
		let requestBody = {}
		try {
			requestBody = JSON.parse(attrs.accessTokenEndpointRequestBody)
		} catch (error) {
			if (!globals.testing) {
				console.warn(
					"Couldn't parse the access token endpoint request body",
					attrs.requestBody, error)
			}
			options.requestBody = attrs.accessTokenEndpointRequestBody
			return options
		}
		// add the refresh_token url parameter and our placeholder that will
		// be replaced on the backend
		requestBody[REFRESH_TOKEN_URL_PARAMETER] = REFRESH_TOKEN_PLACEHOLDER
		requestBody[GRANT_TYPE_URL_PARAMETER] = REFRESH_TOKEN_URL_PARAMETER
		options.requestBody = JSON.stringify(requestBody)

		return options
	},

	saveState: function() {
		RestConnectionWizardView.prototype.saveHttpRequestFormState
			.call(this, TOKEN_OPTIONS.REFRESH_TOKEN)
	},

	shouldValidate: function() {
		// if the connection hasn't been saved yet, it can't possibly have a refresh_token
		// so we will always call the /validate endpoint in the previous view of the wizard
		if (_.isNil(this.model.id)) return false

		const attrs = this.model.attributes

		// this state should not be possible
		if (attrs.wsAuthenticationType !== OAUTH_V2) return false

		// if we have a refresh_token already, we didn't hit the /validate endpoint
		// in the previous step, in order to avoid errors to be able to get to this one,
		// so we always want to call it here if we have a refresh_token
		return true
	},

	submit: function() {
		RestConnectionWizardView.prototype.submitAccessOrRefreshTokenView.call(this)
	}
})

export default RestConnectionRefreshToken
RestConnectionRefreshToken.__filename__ = 'admin/connections/rest/rest-connection-refresh-token.js'
